import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import PricingTable from "@/components/shared/PricingTable";
import FAQSection from "@/components/home/FAQSection";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Server, Zap, Shield, Headphones, HardDrive, Globe } from "lucide-react";

const packages = [
  {
    name: "Starter",
    originalPrice: "1,198",
    price: "599",
    period: "/year",
    description: "Great for startup",
    features: [
      "1 GB NVMe SSD",
      "1 Hosted Website",
      "10 Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "cPanel Access",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-hosting/starter",
  },
  {
    name: "Advanced",
    originalPrice: "1,498",
    price: "749",
    period: "/year",
    description: "Enhanced advance performance",
    features: [
      "2 GB NVMe SSD",
      "1 Hosted Website",
      "20 Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "cPanel Access",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-hosting/advanced",
  },
  {
    name: "Business",
    originalPrice: "1,798",
    price: "899",
    period: "/year",
    description: "Ideal for businesses",
    features: [
      "3 GB NVMe SSD",
      "1 Hosted Website",
      "30 Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "cPanel Access",
    ],
    popular: true,
    orderLink: "https://my.webhostbuybd.com/store/bdix-hosting/business",
  },
  {
    name: "Professional",
    originalPrice: "2,398",
    price: "1,199",
    period: "/year",
    description: "For growing businesses",
    features: [
      "5 GB NVMe SSD",
      "3 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "cPanel Access",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-hosting/professional",
  },
  {
    name: "Enterprise",
    originalPrice: "3,898",
    price: "1,949",
    period: "/year",
    description: "High-traffic websites",
    features: [
      "10 GB NVMe SSD",
      "5 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "cPanel Access",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-hosting/enterprise",
  },
  {
    name: "Ultimate",
    originalPrice: "6,898",
    price: "3,449",
    period: "/year",
    description: "Maximum performance",
    features: [
      "20 GB NVMe SSD",
      "10 Hosted Website",
      "Unlimited Email Account",
      "Unlimited Bandwidth",
      "Free SSL Certificate",
      "cPanel Access",
    ],
    orderLink: "https://my.webhostbuybd.com/store/bdix-hosting/ultimate",
  },
];

const features = [
  { icon: Zap, title: "BDIX Optimized", description: "Ultra-fast Bangladesh connectivity" },
  { icon: HardDrive, title: "NVMe SSD", description: "Enterprise-grade storage" },
  { icon: Shield, title: "Free SSL", description: "Secure encryption included" },
  { icon: Server, title: "LiteSpeed", description: "High-performance servers" },
  { icon: Globe, title: "99.99% Uptime", description: "Guaranteed availability" },
  { icon: Headphones, title: "24/7 Support", description: "Expert assistance" },
];

const hostingFaqs = [
  {
    question: "What is BDIX hosting and why is it important?",
    answer: "BDIX (Bangladesh Internet Exchange) hosting provides ultra-fast connectivity within Bangladesh by routing traffic through local exchange points. This results in significantly lower latency and faster page load times for visitors within Bangladesh, making it ideal for local businesses and websites targeting Bangladeshi audiences.",
  },
  {
    question: "Do you offer free website migration?",
    answer: "Yes! We offer completely free website migration for all our hosting plans. Our expert team will migrate your website, databases, emails, and all files from your current host to our servers with zero downtime.",
  },
  {
    question: "What control panel do you use?",
    answer: "We use cPanel, the industry's most popular and user-friendly control panel. It provides an intuitive interface for managing your website, emails, databases, files, and more. We also offer one-click installers for WordPress, Joomla, and 400+ other applications.",
  },
  {
    question: "What is your uptime guarantee?",
    answer: "We guarantee 99.99% uptime for all our hosting services, backed by our Service Level Agreement (SLA). Our state-of-the-art datacenter infrastructure, redundant power systems, and 24/7 monitoring ensure your website stays online.",
  },
  {
    question: "Do you provide daily backups?",
    answer: "Yes, we perform automated daily backups of all hosting accounts. Backups are stored on separate servers for added security. You can also create manual backups anytime through cPanel or request restoration from our support team.",
  },
  {
    question: "How fast is your customer support?",
    answer: "Our customer support team is available 24/7 via live chat, phone, and ticket system. Average response time for live chat is under 2 minutes, and most tickets are resolved within 1 hour. Our support team consists of certified hosting experts.",
  },
];

const WebHosting = () => {
  return (
    <Layout>
       <SEOHead
         title="Cheapest BDIX Web Hosting Bangladesh | NVMe SSD Shared Hosting - 50% OFF"
         description="Cheapest BDIX web hosting in Bangladesh from ৳599/year. NVMe SSD hosting with LiteSpeed, free SSL &amp; cPanel. Shared hosting Bangladesh with 99.99% uptime. Affordable &amp; reliable web hosting Bangladesh. BDIX shared hosting, web hosting BD."
         canonical="/bdix-web-hosting"
         type="product"
         keywords="fastest BDIX web hosting, cheapest BDIX web hosting, shared hosting Bangladesh, SSD hosting Bangladesh, NVMe SSD hosting, affordable web hosting Bangladesh, reliable web hosting Bangladesh, web hosting Bangladesh, BDIX shared hosting, bdix cloud hosting, web hosting bd, web host bd, bdix web hosting, domain hosting Bangladesh, bdix web host, bdix hosting server, bdix hosting offer"
       />
      <SchemaMarkup
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "BDIX Web Hosting", url: "/bdix-web-hosting" },
        ]}
        faqs={hostingFaqs}
        products={packages.map((p) => ({
          name: `${p.name} - BDIX Web Hosting`,
          description: `${p.description}. ${p.features[0]} NVMe SSD, ${p.features[1]}, ${p.features[5]}, Free SSL, BDIX optimized.`,
          price: p.price,
          orderLink: p.orderLink,
          ratingValue: p.popular ? "4.9" : "4.7",
          reviewCount: p.popular ? "189" : "124",
        }))}
        pageName="BDIX Web Hosting Bangladesh - Affordable NVMe SSD Hosting"
        pageDescription="Cheapest BDIX web hosting Bangladesh with NVMe SSD. Shared hosting from ৳599/year with cPanel, free SSL, and 24/7 support."
      />
      <PageHero
        badge="BDIX Shared Hosting"
        title="Cheapest BDIX Web Hosting in Bangladesh"
        subtitle="Best hosting in Bangladesh with NVMe SSD hosting and shared hosting. Reliable web hosting Bangladesh, affordable web hosting Bangladesh with 24/7 expert support."
      />

      {/* Features Strip */}
      <section className="py-12 bg-muted/30 border-b border-border" aria-label="BDIX Web Hosting Features">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingTable
        packages={packages}
        title="Choose Your BDIX Hosting Plan"
        subtitle="All BDIX hosting plans include free SSL, cPanel access, and 24/7 technical support. Affordable hosting price in Bangladesh."
      />

      <FAQSection />
      <PartnersSection />
    </Layout>
  );
};

export default WebHosting;
